import React, { useState, useEffect, lazy, Suspense } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import MenuProvider from './context/MenuContext';
import { WishlistProvider } from './context/WishlistContext'; // Import WishlistProvider
import AnalyticsRouter from './components/Analytics/AnalyticsRouter'; // Import Analytics router
import ActivityRouter from './components/Analytics/ActivityRouter'; // Import Activity router
import CookieConsentBanner from './components/Consent/CookieConsentBanner';
// DISABLED: Push notifications feature disabled
// import { NotificationPromptManager, PersistentNotificationBanner } from './components/Notifications'; // Import notification prompt
import Layout from './components/Layout/Layout';
import ProductList from './components/Products/ProductList';
import FeaturedProducts from './components/Products/FeaturedProducts'; // Import the new featured products component
import SearchResults from './components/Products/SearchResults'; // Import SearchResults
import PageDetail from './components/Pages/PageDetail';
import BlogList from './components/Blog/BlogList';
import ReviewList from './components/Reviews/ReviewList';
import Auth from './components/Auth/Auth';
import Profile from './components/Profile/Profile';
import Dashboard from './components/Dashboard/Dashboard';
import OrderSuccess from './components/Orders/OrderSuccess';
import OrderCancel from './components/Orders/OrderCancel';
import PrivateRoute from './components/Auth/PrivateRoute';
import AdminRoute from './components/Auth/AdminRoute';
import AdminLayout from './components/Admin/AdminLayout';
import AdminLoadingFallback from './components/Admin/AdminLoadingFallback';
import SEO from './components/SEO/SEO';
import Sitemap from './components/SEO/Sitemap';
import { fetchSiteMetadata } from './services/seoService';
import { CONSENT_CHANGED_EVENT } from './utils/analyticsConsent';
import { applyConsentToAnalytics, initializeAnalyticsIfAllowed } from './utils/analyticsInit';
import { applyConsentToMetaPixel, initializeMetaPixelIfAllowed } from './utils/metaPixel';
import ReviewForm from './pages/Review/ReviewForm'; // Import the new Review Form component
// Import new Homepage components
import HomePage from './pages/HomePage'; // Import HomePage
import HomepagePopup from './components/Homepage/HomepagePopup'; // Import Homepage Popup
import UnsubscribePage from './pages/Unsubscribe/UnsubscribePage'; // Import the new Unsubscribe page
import MaintenanceBanner from './components/Layout/MaintenanceBanner'; // Import Maintenance Banner
import { COLORS, FONTS } from './theme/tokens';

// Lazy-loaded storefront components for code splitting
const ProductDetail = lazy(() => import('./components/Products/ProductDetail'));
const BlogDetail = lazy(() => import('./components/Blog/BlogDetail'));
const Cart = lazy(() => import('./components/Cart/Cart'));
const Checkout = lazy(() => import('./components/Checkout/Checkout'));
const Orders = lazy(() => import('./components/Orders/Orders'));
const OrderDetails = lazy(() => import('./components/Orders/OrderDetails'));
const WishlistPage = lazy(() => import('./pages/Wishlist/WishlistPage'));

// Lazy-loaded Admin Components for code splitting
// These components are only loaded when the user navigates to admin routes
const AdminDashboard = lazy(() => import('./components/Admin/AdminDashboard'));
const AdminOrders = lazy(() => import('./components/Admin/AdminOrders'));
const AdminOrderDetail = lazy(() => import('./components/Admin/AdminOrderDetail'));
const AdminProducts = lazy(() => import('./components/Admin/AdminProducts'));
const AdminProductEdit = lazy(() => import('./components/Admin/AdminProductEdit'));
const AdminGallery = lazy(() => import('./components/Admin/AdminGallery'));
const AdminCategories = lazy(() => import('./components/Admin/AdminCategories'));
const CategoryForm = lazy(() => import('./components/Admin/CategoryForm'));
const AdminAttributes = lazy(() => import('./components/Admin/AdminAttributes'));
const AttributeForm = lazy(() => import('./components/Admin/AttributeForm'));
const AdminReviews = lazy(() => import('./components/Admin/AdminReviews'));
const AdminBlogList = lazy(() => import('./components/Admin/AdminBlogList'));
const AdminBlogEdit = lazy(() => import('./components/Admin/AdminBlogEdit'));
const VariationsList = lazy(() => import('./pages/Admin/Variations/VariationsList'));
const VariationForm = lazy(() => import('./pages/Admin/Variations/VariationForm'));
const AdminSettings = lazy(() => import('./components/Admin/AdminSettings'));
const AdminTranslations = lazy(() => import('./components/Admin/AdminTranslations'));
const AdminCustomerMessaging = lazy(() => import('./components/Admin/AdminCustomerMessaging'));
const AdminCustomers = lazy(() => import('./components/Admin/AdminCustomers'));
const AdminCoupons = lazy(() => import('./components/Admin/AdminCoupons'));
const CouponForm = lazy(() => import('./components/Admin/CouponForm'));
const AdminPagesList = lazy(() => import('./components/Admin/AdminPagesList'));
const AdminPageForm = lazy(() => import('./components/Admin/AdminPageForm'));
const AdminCalendar = lazy(() => import('./components/Admin/AdminCalendar'));
const AdminReports = lazy(() => import('./components/Admin/AdminReports'));
const AdminPushNotifications = lazy(() => import('./components/Admin/AdminPushNotifications'));
const AdminDeviceDetail = lazy(() => import('./components/Admin/AdminDeviceDetail'));
const AdminSendNotification = lazy(() => import('./components/Admin/AdminSendNotification'));
const AdminNotificationsAnalytics = lazy(() => import('./components/Admin/AdminNotificationsAnalytics'));

function NotFoundPage() {
  return (
    <Box sx={{ minHeight: '58vh', py: { xs: 8, md: 12 }, px: 2, textAlign: 'center', bgcolor: COLORS.surfacePanel }}>
      <Typography sx={{ color: COLORS.goldText, fontWeight: 700, letterSpacing: '0.18em', mb: 2 }}>
        404
      </Typography>
      <Typography variant="h3" component="h1" gutterBottom sx={{ fontFamily: FONTS.heading, fontWeight: 600 }}>
        This page has come untied
      </Typography>
      <Typography color="text.secondary" sx={{ maxWidth: 480, mx: 'auto', lineHeight: 1.7 }}>
        The requested page does not exist or may have moved.
      </Typography>
    </Box>
  );
}

// Define fonts first
const headingFont = "'Playfair Display', serif";
const bodyFont = "'Lato', sans-serif";

const theme = createTheme({
  shape: {
    borderRadius: 2,
  },
  palette: {
    primary: {
      main: '#3A4F41', // Deep Green
      dark: '#2C3E33',
    },
    secondary: {
      main: '#B08D57', // Soft Gold
      dark: '#96754A',
    },
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#212121', // Dark Grey for primary text
      secondary: '#757575', // Medium Grey for secondary text
    },
    divider: '#ECE7DE',
    error: {
      main: '#D32F2F', // Standard MUI Red
    },
    // Add other palette options as needed (info, warning, success)
  },
  typography: {
    fontFamily: bodyFont,
    h1: { fontFamily: headingFont, fontWeight: 600 },
    h2: { fontFamily: headingFont, fontWeight: 600 },
    h3: { fontFamily: headingFont, fontWeight: 600 },
    h4: { fontFamily: headingFont, fontWeight: 600 },
    h5: { fontFamily: headingFont, fontWeight: 600 },
    h6: { fontFamily: headingFont, fontWeight: 600 },
    button: {
      textTransform: 'none', // Prevent uppercase buttons by default
      fontWeight: 600,
    },
    // Ensure body fonts are set correctly
    body1: { fontFamily: bodyFont },
    body2: { fontFamily: bodyFont },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          textTransform: 'uppercase',
          fontSize: '0.8125rem',
          fontWeight: 700,
          letterSpacing: '1.5px',
          padding: '12px 24px',
        },
        containedPrimary: {
          color: '#FFFFFF',
          '&:hover': { backgroundColor: '#2C3E33' },
        },
        containedSecondary: {
          color: '#FFFFFF',
          '&:hover': { backgroundColor: '#96754A' },
        },
        outlined: { borderWidth: '1px' },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: '1px solid #ECE7DE',
          borderRadius: 2,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 2,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none', // Default to no shadow for AppBars
        },
        colorDefault: {
          backgroundColor: '#FFFFFF', // Ensure default AppBar is white
          color: '#212121', // Ensure text color contrasts with white
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#3A4F41', // Use primary color for links
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        },
      },
    },
    // Add other component overrides as needed
  },
});

// DISABLED: Push notifications feature disabled
// function NotificationWrapper() {
//   const { user } = useAuth();
//
//   return (
//     <>
//       {/* Timed popup - shows after 30 seconds */}
//       <NotificationPromptManager
//         user={user}
//         trigger="delayed"
//         delaySeconds={30}
//       />
//       {/* Persistent banner - always visible until enabled or dismissed */}
//       <PersistentNotificationBanner
//         user={user}
//         position="floating" // Options: 'top', 'bottom', 'floating'
//       />
//     </>
//   );
// }

function App() {
  const [globalSEO, setGlobalSEO] = useState(null);
  // Fetch global site metadata
  useEffect(() => {
    const getGlobalSEO = async () => {
      const data = await fetchSiteMetadata();
      if (data) {
        setGlobalSEO(data);
      }
    };
    getGlobalSEO();

  }, []);

  useEffect(() => {
    initializeAnalyticsIfAllowed();
    // Meta is marketing-only; separate from GA4 analytics consent path.
    initializeMetaPixelIfAllowed();

    const handleConsentChanged = (event) => {
      initializeAnalyticsIfAllowed();
      applyConsentToAnalytics(event.detail);
      initializeMetaPixelIfAllowed();
      applyConsentToMetaPixel(event.detail);
    };

    window.addEventListener(CONSENT_CHANGED_EVENT, handleConsentChanged);
    return () => {
      window.removeEventListener(CONSENT_CHANGED_EVENT, handleConsentChanged);
    };
  }, []);


  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <HelmetProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline /> {/* Restore CssBaseline */}
          {/* Add global SEO data - Restore conditional rendering */}
          {globalSEO && globalSEO.defaultMetaTags && (
            <React.Fragment>
              <SEO 
                meta={globalSEO.defaultMetaTags}
                structuredData={globalSEO.websiteSchema}
              />
            </React.Fragment>
          )}
          {globalSEO && globalSEO.organizationSchema && (
             <React.Fragment>
               <SEO 
                 structuredData={globalSEO.organizationSchema}
               />
             </React.Fragment>
          )}
          <AuthProvider>
          <WishlistProvider> {/* Add WishlistProvider here */}
            <MenuProvider>
              {/* Handles GA4 pageviews on route changes after consent */}
              <AnalyticsRouter />
              <CookieConsentBanner />
              {/* Add Activity Tracker component - handles browsing activity tracking */}
              <ActivityRouter />
              <HomepagePopup /> {/* Add Homepage Popup - shows on all pages */}
              {/* DISABLED: Push notifications feature disabled */}
              {/* <NotificationWrapper /> */}
              <MaintenanceBanner /> {/* Add Maintenance Banner - shows when maintenance mode is enabled */}
              <Layout>
              <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/featured" element={<FeaturedProducts />} /> {/* Add route for featured products */}
              <Route path="/products" element={<Navigate to="/featured" replace />} />
              <Route path="/search" element={<SearchResults />} /> {/* Add Search Results Route */}
              <Route path="/category/:slug" element={<ProductList />} />
              <Route path="/product-category/:id" element={<ProductList attributeProducts={true} />} />
              <Route path="/product/:slug" element={<Suspense fallback={null}><ProductDetail /></Suspense>} />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<Suspense fallback={null}><BlogDetail /></Suspense>} />
              <Route path="/reviews" element={<ReviewList headingLevel="h1" />} />
              <Route path="/cart" element={<Suspense fallback={null}><Cart /></Suspense>} />
              <Route path="/wishlist" element={<Suspense fallback={null}><WishlistPage /></Suspense>} />
              <Route path="/checkout" element={<Suspense fallback={null}><Checkout /></Suspense>} />
              <Route path="/order-success" element={<OrderSuccess />} />
              <Route path="/order-cancel" element={<OrderCancel />} />
              <Route path="/login" element={<Auth />} />
              <Route path="/register" element={<Auth />} />
              <Route path="/unsubscribe" element={<UnsubscribePage />} /> {/* Add Unsubscribe Route */}
              <Route path="/sitemap.xml" element={<Sitemap />} />
              {/* User Routes - Require Authentication */}
              <Route path="/dashboard" element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              } />
              <Route path="/profile" element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              } />
              <Route path="/my-orders" element={
                <PrivateRoute>
                  <Suspense fallback={null}><Orders /></Suspense>
                </PrivateRoute>
              } />
              <Route path="/orders/:orderId" element={
                <PrivateRoute>
                  <Suspense fallback={null}><OrderDetails /></Suspense>
                </PrivateRoute>
              } />
              {/* Review Form Route - Login-free access */}
              <Route path="/review/:orderNumber" element={<ReviewForm />} />
              {/* Admin Routes - Require is_admin >= 1 */}
              {/* Admin components are lazy-loaded for improved initial load performance */}
              <Route path="/admin" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminDashboard />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/orders" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminOrders />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/orders/:orderId" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminOrderDetail />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/products" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminProducts />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/products/:productId/edit" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminProductEdit />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/new" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminProductEdit />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/gallery" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminGallery />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/categories" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminCategories />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              {/* Make sure the 'new' route comes before the :id route */}
              <Route path="/admin/categories/new" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <CategoryForm />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/categories/:id/edit" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <CategoryForm />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              {/* Attributes Routes */}
              <Route path="/admin/attributes" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminAttributes />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/attributes/new" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AttributeForm />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/attributes/:id/edit" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AttributeForm />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              {/* Reviews Admin Route */}
              <Route path="/admin/reviews" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminReviews />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              {/* Blog Admin Routes */}
              <Route path="/admin/blog" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminBlogList />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/blog/create" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminBlogEdit isNew={true} />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/blog/edit/:id" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminBlogEdit />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              {/* Variations Admin Routes */}
              <Route path="/admin/variations" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <VariationsList />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/variations/parent/:parentId" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <VariationsList />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/variations/create" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <VariationForm />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/variations/create/:parentId" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <VariationForm />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/variations/edit/:id" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <VariationForm />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />

              {/* Messaging Admin Route */}
              <Route path="/admin/messaging" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminCustomerMessaging />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />

              {/* Coupons Admin Routes */}
              <Route path="/admin/coupons" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminCoupons />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/coupons/create" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <CouponForm />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/coupons/edit/:couponId" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <CouponForm />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />

              {/* Customers Admin Route */}
              <Route path="/admin/customers" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminCustomers />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />

              {/* Settings Admin Route */}
              <Route path="/admin/settings" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminSettings />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />

              {/* Translations hub */}
              <Route path="/admin/translations" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminTranslations />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />

              {/* Content Pages Admin Routes */}
              <Route path="/admin/pages" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminPagesList />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />

              {/* Calendar Admin Route */}
              <Route path="/admin/calendar" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminCalendar />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/pages/new" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminPageForm />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/pages/edit/:id" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminPageForm />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              {/* Email Template Routes REMOVED - Using file templates now */}

              {/* Reports Admin Route */}
              <Route path="/admin/reports" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminReports />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />

              {/* Push Notifications Admin Routes */}
              <Route path="/admin/push-notifications" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminPushNotifications />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/push-notifications/device/:id" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminDeviceDetail />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/push-notifications/send" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminSendNotification />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/push-notifications/analytics" element={
                <AdminRoute>
                  <AdminLayout>
                    <Suspense fallback={<AdminLoadingFallback />}>
                      <AdminNotificationsAnalytics />
                    </Suspense>
                  </AdminLayout>
                </AdminRoute>
              } />

              {/* Always put this route last */}
              <Route path="/:slug" element={<PageDetail />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
              </Layout>
            </MenuProvider>
          </WishlistProvider> {/* Close WishlistProvider */}
        </AuthProvider>
        </ThemeProvider>
        {/* SEO Meta Checker removed for debugging */}
      </HelmetProvider>
    </BrowserRouter>
  );
}
export default App;
